import { nameof } from 'ts-simple-nameof'
import * as yup from 'yup'
import type { Offer, PersonAdditionalData, PersonDto, PolicyHolderAdditionalData, PolicyHolderDto } from '../dtos'
import { Insurer, Selection } from '../enums'
import { isIn } from '../Utils/comparisonUtils'
import { AddressValidationSchema, Step4AddressValidationSchema } from './address'
import { PropertyAddressValidationRuleObject } from './property/PropertyAddressValidationSchema'

const typeRequiredSchemaOpt = (expectedType: Selection, message: string) => {
    return {
        is: (givenType: string) => givenType && isIn(parseInt(givenType) as Selection, expectedType, Selection.SelfEmployed),
        then: () => yup.string().required(message).min(1, message),
        otherwise: () => yup.string().nullable()
    }
}

export const PersonValidationSchema = {
    type: yup.number().required('Vyberte typ osoby'),
    name: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte jméno')),
    surname: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte příjmení')),
    idendificationNumber: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte IČO')),
    companyName: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte název')),
    birthNumber: yup.string().when([nameof<PersonDto>(x => x.type), nameof<PersonDto>(x => x.isForeigner)], {
        is: (givenType: string, isForeigner: boolean | undefined) => givenType &&  //z K4 se někdy vrací isForeigner jako undefined i když už je na K1 rč nebo datum narození uloženo
            isForeigner !== undefined && !isForeigner && isIn(parseInt(givenType), Selection.Person, Selection.SelfEmployed),
        then: () => yup.string().matches(/^\d*$/, 'Rodné číslo musí obsahovat pouze číslice').required('Zadejte rodné číslo').min(1, 'Zadejte rodné číslo'),
        otherwise: () => yup.string().nullable().matches(/^\d*$/, 'Rodné číslo musí obsahovat pouze číslice')
    }),
    birthday: yup.string().when([nameof<PersonDto>(x => x.type), nameof<PersonDto>(x => x.isForeigner)], {
        is: (givenType: string, isForeigner: boolean | undefined) => givenType &&
            isForeigner !== undefined && isForeigner && isIn(parseInt(givenType), Selection.Person, Selection.SelfEmployed),
        then: () => yup.string().required('Zadejte datum narození'),
        otherwise: () => yup.string().nullable()
    }),
    //address: yup.object({ ...AddressValidationSchema }), // nove je vsude ruian. Pouze v cestovnim zustala tato puvodni
    address: yup.object({ ...PropertyAddressValidationRuleObject }),
}

export const PersonValidationSchemaWithPhoneNumbers = {
    ...PersonValidationSchema,
    mobilePhone: yup.string()
        .nullable()
        .test(
            'no-prefix',
            'Zadejte číslo bez předvolby, předvolbu vyberte vedle',
            value => {
                // Allow null or empty string.
                if (!value) return true
                // Předvolba se vybírá v samostatném selectu - v čísle nesmí být ("+420...", "00420...", "420704604484").
                const digits = value.replace(/\s/g, '')
                return !/^(\+|00)/.test(digits) && !/^42[01]\d{9}$/.test(digits)
            }
        )
        .test(
            'is-not-landline',
            'Zadejte číslo mobilního telefonu',
            value => {
                // Allow null or empty string.
                if (!value) return true
                // Fail the test if the value matches the fixed phone regex.
                return !/^[234589](?:\s*\d){8}$/.test(value)
            }
        ),
    phone: yup.string().nullable().matches(/^(?:\+420\s*)?[234589](?:\s*\d){8}$/, { message: 'Zadejte číslo pevné linky', excludeEmptyString: true }),
}

const personSameAsDependentRequiredSchema = {
    is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder || sameAs === Selection.PersonSameAsKeeper,
    then: () => yup.object().optional().nullable(),
    otherwise: () => yup.object({ ...PersonValidationSchema }).nullable()
}

export const PolicyHolderValidationRuleObject = {
    person: yup.object({ ...PersonValidationSchema }),
    holder: yup.object().when(nameof<PolicyHolderDto>(x => x.holderSameAs), personSameAsDependentRequiredSchema),
    owner: yup.object().when(nameof<PolicyHolderDto>(x => x.ownerSameAs), personSameAsDependentRequiredSchema),
}

export const PolicyHolderTravelValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object({ ...AddressValidationSchema })
    }),
    holder: yup.object().when(nameof<PolicyHolderDto>(x => x.holderSameAs), personSameAsDependentRequiredSchema),
    owner: yup.object().when(nameof<PolicyHolderDto>(x => x.ownerSameAs), personSameAsDependentRequiredSchema),
}

// Any of mobilePhone, phone, email is required
export const PersonValidationSchemaAnyContact = {
    mobilePhone: yup.string().when([nameof<PersonAdditionalData>(x => x.phone), nameof<PersonAdditionalData>(x => x.email)], {
        is: (phone: string, email: string) => !phone && !email,
        then: () => yup.string().required('Vyplňte alespoň jeden kontakt'),
        otherwise: () => yup.string().nullable()
    }),
    phone: yup.string().when([nameof<PersonAdditionalData>(x => x.mobilePhone), nameof<PersonAdditionalData>(x => x.email)], {
        is: (mobilePhone: string, email: string) => !mobilePhone && !email,
        then: () => yup.string().required('Vyplňte alespoň jeden kontakt'),
        otherwise: () => yup.string().nullable()
    }),
    email: yup.string().when([nameof<PersonAdditionalData>(x => x.mobilePhone), nameof<PersonAdditionalData>(x => x.phone)], {
        is: (mobilePhone: string, phone: string) => !mobilePhone && !phone,
        then: () => yup.string().required('Vyplňte alespoň jeden kontakt'),
        otherwise: () => yup.string().nullable()
    }),
}

export const PersonValidationSchemaWithRequiredContact = {
    mobilePhone: yup.string().required('Pro vybranou pojišťovnu je mobil povinný.'),
    phone: yup.string().nullable(),
    email: yup.string().required('Pro vybranou pojišťovnu je email povinný.'),
}

export const AdditionalPolicyHolderValidationRuleObject = (context?: AdditionalInfoValidationSchemaContext) => {
    const gcpAnyContact = context?.offer.insurerId === Insurer.GeneraliCeskaPojistovna ? PersonValidationSchemaAnyContact : {}
    const koopRequitedContact = context?.offer.insurerId === Insurer.Kooperativa ? PersonValidationSchemaWithRequiredContact : {}

    return ({
        person: yup.object().shape({ ...PersonValidationSchemaWithPhoneNumbers, ...gcpAnyContact, ...koopRequitedContact },
            [
                ['email', 'phone'],
                ['email', 'mobilePhone'],
                ['phone', 'mobilePhone'],
                ['phone', 'email'],
            ]
        ),
        holder: yup.object().when(nameof<PolicyHolderDto>(x => x.holderSameAs), {
            is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder,
            then: () => yup.object().optional().nullable(),
            otherwise: () => yup.object({
                ...PersonValidationSchemaWithPhoneNumbers,
                address: yup.object().optional().nullable(),
            }).nullable()
        }),
        owner: yup.object().when(nameof<PolicyHolderDto>(x => x.ownerSameAs), {
            is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder || sameAs === Selection.PersonSameAsKeeper,
            then: () => yup.object().optional().nullable(),
            otherwise: () => yup.object({
                ...PersonValidationSchemaWithPhoneNumbers,
                address: yup.object().optional().nullable(),
            }).nullable()
        }),
    })
}

export const PolicyHolderPropertyValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object().optional().nullable(),
    }),
    insuredPerson: yup.object().when(nameof<PolicyHolderDto>(x => x.insuredPersonSameAs), {
        is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...PersonValidationSchema,
            address: yup.object().optional().nullable(),
        }).nullable()
    }),
}

export const PolicyHolderLiabilityValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object({ ...PropertyAddressValidationRuleObject }),
    }),
    insuredPerson: yup.object().when(nameof<PolicyHolderDto>(x => x.insuredPersonSameAs), {
        is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...PersonValidationSchema,
            address: yup.object({ ...PropertyAddressValidationRuleObject }),
        }).nullable()
    }),
}

export const ContactPersonValidationSchema = {
    type: yup.number().required('Vyberte typ osoby'),
    name: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte jméno')),
    surname: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte příjmení')),
    companyName: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte název')),
    address: yup.object({ ...Step4AddressValidationSchema }),
}

export const PolicyHolderPropertyStep4ValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchemaWithPhoneNumbers,
        address: yup.object({ ...Step4AddressValidationSchema }),
    }),
    insuredPerson: yup.object().when(nameof<PolicyHolderDto>(x => x.insuredPersonSameAs), {
        is: (sameAs: any) => sameAs && Number(sameAs) === Selection.PersonSameAsPolicyHolder,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...PersonValidationSchema,
            address: yup.object({ ...Step4AddressValidationSchema }),
        }).nullable()
    }),
    contact: yup.object().when(nameof<PolicyHolderAdditionalData>(x => x.contactSameAs), {
        is: (sameAs: any) => sameAs && (Number(sameAs) === Selection.PersonSameAsPolicyHolder
            || Number(sameAs) === Selection.PersonSameAsClient),
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...ContactPersonValidationSchema
        }).nullable()
    }),
}

export const PolicyHolderValidationSchema = yup.object().shape(PolicyHolderValidationRuleObject) as any
export const AdditionalPolicyHolderValidationSchema = (context?: AdditionalInfoValidationSchemaContext) => yup.object().shape(AdditionalPolicyHolderValidationRuleObject(context)) as any
export const PolicyHolderTravelValidationSchema = yup.object().shape(PolicyHolderTravelValidationRuleObject) as any
export const PolicyHolderPropertyValidationSchema = yup.object().shape(PolicyHolderPropertyValidationRuleObject) as any
export const PolicyHolderLiabilityValidationSchema = yup.object().shape(PolicyHolderLiabilityValidationRuleObject) as any
export const PolicyHolderPropertyStep4ValidationSchema = yup.object().shape(PolicyHolderPropertyStep4ValidationRuleObject) as any
export const PolicyHolderPublicPoleasValidationSchema = yup.object().shape({
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object({ ...Step4AddressValidationSchema }),
    }),
    owner: yup.object().when(nameof<PolicyHolderDto>(x => x.ownerSameAs), personSameAsDependentRequiredSchema),
}) as any

export interface AdditionalInfoValidationSchemaContext {
    offer: Offer
    extra?: boolean
}